import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

/**
 * A shared confirmation dialog with an optional typed-confirmation guard.
 *
 * <p>Use this for any action that is destructive, externally visible, or
 * hard to reverse. The typed-confirmation (user must retype a specific
 * phrase to enable the action button) is the guard against muscle-memory
 * clicks on production clusters.
 *
 * <pre>
 * &lt;ConfirmDialog
 *   open={open}
 *   title="Delete cluster"
 *   severity="error"
 *   message="This removes the cluster entity and unassigns every node."
 *   confirmPhrase={cluster.name}
 *   confirmLabel="Delete"
 *   onConfirm={async () =&gt; await api.delete(...)}
 *   onClose={() =&gt; setOpen(false)}
 * /&gt;
 * </pre>
 *
 * @param open              whether the dialog is visible
 * @param title             dialog heading
 * @param message           string or React node describing the action
 * @param severity          MUI Alert severity for the banner — 'warning' (default) or 'error'
 * @param confirmPhrase     if present, user must retype this string verbatim to enable the action
 * @param confirmLabel      button label (default 'Confirm')
 * @param cancelLabel       cancel button label (default 'Cancel')
 * @param destructive       when true, the confirm button is color="error"; defaults to true for severity='error'
 * @param onConfirm         async callback; the dialog disables the button while it runs
 * @param onClose           called when the user cancels OR after a successful confirm
 * @param extraContent      optional extra JSX rendered above the confirm input (e.g. a checkbox)
 */
const ConfirmDialog = ({
  open,
  title,
  message,
  severity = 'warning',
  confirmPhrase,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive,
  onConfirm,
  onClose,
  extraContent,
}) => {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset every time the dialog opens so a stale typed value doesn't auto-enable the button.
  useEffect(() => {
    if (open) {
      setTyped('');
      setBusy(false);
    }
  }, [open]);

  const needsTyping = Boolean(confirmPhrase);
  const canSubmit = !busy && (!needsTyping || typed === confirmPhrase);
  const isDestructive = destructive !== undefined ? destructive : severity === 'error';

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm?.();
      onClose?.();
    } catch (e) {
      // Leave the dialog open so the caller's onConfirm can surface the error to the user.
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose?.()} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color={severity} />
          <Typography variant="h6" component="span">{title}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity={severity} sx={{ mb: 2 }}>
          {typeof message === 'string' ? <Typography variant="body2">{message}</Typography> : message}
        </Alert>
        {extraContent}
        {needsTyping && (
          <TextField
            autoFocus
            fullWidth
            sx={{ mt: extraContent ? 2 : 0 }}
            label={`Type "${confirmPhrase}" to confirm`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) handleConfirm(); }}
            placeholder={confirmPhrase}
            spellCheck={false}
            autoComplete="off"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => !busy && onClose?.()} disabled={busy}>{cancelLabel}</Button>
        <Button
          onClick={handleConfirm}
          disabled={!canSubmit}
          variant="contained"
          color={isDestructive ? 'error' : 'primary'}
        >
          {busy ? 'Working…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
